<template>
  <div class="seo-page">
    <!-- En-tête avec dégradé, sans la navigation en double -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Nos solutions de référencement SEO</h1>
        <p class="subtitle">Boostez votre identité en ligne avec une stratégie SEO sur-mesure</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Dopez votre rayonnement digital avec notre expertise en référencement SEO</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                Dans un environnement digital hautement concurrentiel, notre agence se positionne comme 
                le partenaire stratégique idéal pour transformer votre visibilité en ligne. Spécialisés dans 
                la conception et la mise en œuvre de stratégies SEO performantes, nous offrons des solutions 
                sur-mesure qui allient efficacité et durabilité, avec les dernières techniques disponibles. 
                Avec nos services de référencement naturel, dynamisez votre présence en ligne, 
                optimisez l'acquisition de trafic qualifié et améliorez la performance globale de votre site internet professionnel.
              </p><br>
              <p>
                ✓ <strong><i>UX / UI</i></strong> : Créez un site web à la pointe en termes d'architecture et d'expérience utilisateur.
              </p><br>
              <p>
                ✓ <strong><i>Contenu optimisé</i></strong> : Positionnez-vous sur les requêtes clés et répondez aux attentes de vos visiteurs.
              </p><br>
              <p>
                ✓ <strong><i>Netlinking</i></strong> : Améliorez votre présence en ligne avec des campagnes de backlinks.
              </p>
            </div>
            <div class="illustration-container">
              <iframe 
                src="https://www.youtube.com/embed/b8UHJ8ORkMY" 
                title="Vidéo explicative SEO" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
                class="youtube-video">
              </iframe>
            </div>
          </div>
        </div>
      </section>

      <!-- Qu'est-ce que le SEO -->
      <section class="card what-is-card">
        <div class="card-content">
          <h2 class="section-title">Qu'est-ce que le SEO ?</h2>
          <div class="text-content">
            <p>
              Le référencement naturel (SEO) consiste essentiellement à rendre un site web visible sur les 
              moteurs de recherche. Pour y parvenir, il est nécessaire de structurer le contenu de manière 
              cohérente et de le compléter en ajoutant des balises aux images, des mots-clés aux titres et 
              des liens vers des sources externes. Nous pouvons aussi créer des backlinks, ce qui aide à 
              améliorer le classement de votre site web. Notre approche combine analyses techniques et 
              stratégiques pour garantir un référencement pérenne et efficace.
            </p>
          </div>
        </div>
      </section>

      <!-- Pourquoi choisir nos services SEO -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">Pourquoi faire appel à nos services pour votre SEO ?</h2>
          <div class="text-content">
            <p>
              Avec nos solutions SEO, transformez radicalement votre visibilité en ligne et boostez votre business. 
              Pour renforcer votre présence digitale et générer un trafic qualifié sur votre site professionnel, 
              investissez dans nos stratégies SEO, conçues avec les méthodologies les plus en pointe, adaptées à vos besoins et à votre domaine d'activité.              
            </p>
          </div>
        </div>

        <div align="center">
          <a href="https://www.web-passion.fr/prestations/" target="_blank"
            style="display: block; width: fit-content; margin: 0 auto; background-color: #7c3aed1a; color: inherit; padding: 10px; border-radius: 9999px;">
            <strong>En savoir plus sur nos prestations SEO</strong>
          </a><br>
        </div>

      </section>

      <!-- Nos résultats -->
      <section class="results-section">
        <h2 class="results-title">Nos résultats, à quoi pouvez-vous vous attendre</h2>
        
        <div class="results-container">
          <!-- Contenu du graphique et du texte -->
          <div class="results-content">
            <!-- Colonne graphique -->
            <div class="results-left">
              <img src="/img/traffic-trend.svg" alt="Tendance d'estimation de trafic SEO" class="results-graph" />
            </div>
            
            <!-- Colonne texte -->
            <div class="results-right">
              <p class="results-text">
                Notre approche stratégique du référencement naturel produit des résultats mesurables et durables. 
                Comme le montre le graphique ci-contre, nos clients constatent généralement une augmentation 
                significative du trafic organique dès la prise en main du référencement, avec une croissance 
                régulière pendant toute la durée de notre collaboration.
              </p>
            </div>
          </div>
          
          <!-- Statistiques -->
          <div class="stats-container">
            <div class="stat-item">
              <div class="stat-value">+200%</div>
              <div class="stat-label">Augmentation moyenne du trafic</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-value">6 mois</div>
              <div class="stat-label">Délai moyen pour des résultats significatifs</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-value">95%</div>
              <div class="stat-label">Taux de satisfaction client</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Avantages (Cartes) -->
      <div class="advantages-grid">
        <!-- Avantage 1 -->
        <div class="advantage-card">
          <div class="advantage-number">1</div>
          <h3 class="advantage-title">Développer votre notoriété sur le web</h3>
          <p class="advantage-description">
            Nos stratégies SEO permettent de développer significativement la notoriété de votre entreprise
            en ligne. Grâce à un positionnement optimal sur les mots-clés stratégiques, votre marque gagne
            en visibilité et en crédibilité auprès de votre audience cible.
          </p>
        </div>

        <!-- Avantage 2 -->
        <div class="advantage-card">
          <div class="advantage-number">2</div>
          <h3 class="advantage-title">Augmenter votre trafic qualifié</h3>
          <p class="advantage-description">
            Notre approche SEO cible spécifiquement les utilisateurs intéressés par vos produits ou services,
            générant ainsi un trafic hautement qualifié. Les visiteurs attirés par le référencement naturel
            sont plus susceptibles de devenir des prospects.
          </p>
        </div>

        <!-- Avantage 3 -->
        <div class="advantage-card">
          <div class="advantage-number">3</div>
          <h3 class="advantage-title">Faire croître votre taux de conversion</h3>
          <p class="advantage-description">
            Grâce à notre expertise en optimisation de l'expérience utilisateur combinée au SEO, nous
            augmentons non seulement votre trafic mais aussi votre taux de conversion. Notre stratégie
            globale vise à transformer les visiteurs en clients.
          </p>
        </div>

        <!-- Avantage 4 -->
        <div class="advantage-card">
          <div class="advantage-number">4</div>
          <h3 class="advantage-title">Diminuer vos frais publicitaires</h3>
          <p class="advantage-description">
            Le référencement naturel offre un excellent ROI à long terme. Contrairement aux campagnes
            publicitaires payantes, le SEO génère un trafic continu sans coût par clic, réduisant
            significativement vos investissements en publicité.
          </p>
        </div>

        <!-- Avantage 5 -->
        <div class="advantage-card">
          <div class="advantage-number">5</div>
          <h3 class="advantage-title">Assurer un site web bien positionné durablement</h3>
          <p class="advantage-description">
            Nos stratégies SEO sont conçues pour des résultats durables. Nous mettons en place des
            fondations solides qui continuent de porter leurs fruits sur le long terme, assurant
            une présence en ligne pérenne et une croissance régulière.
          </p>
        </div>

        <!-- Avantage 6 -->
        <div class="advantage-card">
          <div class="advantage-number">6</div>
          <h3 class="advantage-title">Améliorer votre expérience client</h3>
          <p class="advantage-description">
            Le SEO ne se limite pas au référencement. Nos optimisations améliorent également l'expérience
            utilisateur de votre site, avec des temps de chargement optimisés, une navigation intuitive
            et des contenus pertinents qui répondent aux attentes de vos visiteurs.
          </p>
        </div>
      </div>

      <!-- Nos services SEO -->
      <h2 class="services-title">Ce que nous offrons pour le SEO</h2>
      
      <!-- Grille de services -->
      <div class="services-grid">
        <!-- Service 1 -->
        <div class="service-card">
          <h3 class="service-title">Audit complet</h3>
          <p class="service-description">
            Notre objectif est de procéder à une analyse approfondie de votre site internet et de nous concentrer 
            ensuite sur les actions ayant le plus d'impact sur votre référencement. Nos audits couvrent l'accessibilité 
            technique, la qualité du contenu et la popularité de votre site.
          </p>
          <ul class="service-features">
            <li>Audit technique pour les robots et utilisateurs</li>
            <li>Audit de contenu pour engagement et pertinence</li>
            <li>Audit de popularité pour légitimité thématique</li>
          </ul>
        </div>
        
        <!-- Service 2 -->
        <div class="service-card">
          <h3 class="service-title">Accompagnement OnSite</h3>
          <p class="service-description">
            Nous cherchons à découvrir de nouvelles opportunités pour votre site internet en ajoutant 
            des actions correctives complémentaires. Notre approche OnSite optimise tous les aspects 
            internes de votre site pour maximiser son potentiel de référencement.
          </p>
          <ul class="service-features">
            <li>Optimisation du contenu</li>
            <li>Livrables réguliers</li>
            <li>Échanges réguliers</li>
          </ul>
        </div>
        
        <!-- Service 3 -->
        <div class="service-card">
          <h3 class="service-title">Accompagnement OffSite</h3>
          <p class="service-description">
            L'objectif principal est de procéder à une évaluation des liens entrants existants et de développer 
            de nouveaux liens vers les pages stratégiques de votre site. Notre stratégie OffSite renforce 
            l'autorité et la crédibilité de votre domaine.
          </p>
          <ul class="service-features">
            <li>Stratégie de netlinking</li>
            <li>Génération de backlinks qualifiés</li>
            <li>Optimisation de popularité</li>
          </ul>
        </div>
      </div>

      <!-- Témoignages clients -->
      <section class="testimonials-section">
        <h2 class="testimonials-title">Ce que disent nos clients</h2>
        
        <div class="testimonials-container">
          <!-- Témoignage 1 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Charles R.</h3>
                <p class="client-company">Agent immobilier</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Suite à ma collaboration avec l'agence Arynae pour la rédaction de contenu SEO, 
                je suis pleinement satisfait des résultats obtenus. L'équipe a su parfaitement comprendre les enjeux de mon business 
                et les briefs très complets ont respecté ma ligne éditoriale. 
                Le délai de livraison a été respecté et la communication a été fluide tout au long du projet. 
                Le pourcentage de pages web positionnées est passé de 10% à 45% en quelques mois. Je recommande vivement leurs services de rédaction 
                SEO.
              </p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Témoignage 2 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Myriam H.</h3>
                <p class="client-company">Gérante d'une boutique de e-commerce</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>L'équipe d'Arynae m'a accompagnée pour la refonte complète et la migration de ma boutique en ligne. 
                Problèmes techniques, contenu, design nous avons tout passé en revue. 
                Après avoir quelques semaines de restructuration, la migration s'est déroulée sans accroc. 
                Six semaines après la mise en ligne, mon trafic organique a augmenté de 45% et mes conversions de 30%.
              </p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Témoignage 3 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Émilie D.</h3>
                <p class="client-company">Chargée de communication</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>J'ai fait appel à Arynae pour développer ma stratégie de netlinking et je ne regrette pas ce choix. 
                Leur approche qualitative plutôt que quantitative m'a convaincu dès le départ. 
                L'agence a identifié des sites pertinents dans mon domaine d'activité et a réussi à obtenir des backlinks de 
                qualité : en l'espace de deux ans, l'autorité de mon site est passé de 15 à 47. 
                J'apprécie particulièrement les rapports détaillés sur les liens obtenus et leur impact. 
                Les résultats sont au rendez-vous avec une progression constante dans les SERP 
                depuis le début de la campagne. Une collaboration professionnelle que je compte poursuivre.
              </p>
            </div>
            <div class="testimonial-border"></div>
          </div>
        </div>
        
        <div class="dots-navigation">
          <span class="dot active"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </section>

      <!-- Call to action -->
      <div class="cta-container">
        <span class="cta-button fake-link" @click="navigateTo('e1')">Prenez un rendez-vous en ligne / téléphonique / vidéo</span>
      </div>
    </div>
  </div>
</template>


<script>
import { useHead } from '@vueuse/head'

export default {
  name: 'SeoServicePage',
  setup() {
    useHead({
      title: 'Services SEO - Experts en référencement naturel | Arynae',
      meta: [
        { name: 'description', content: 'Découvrez nos services de référencement naturel SEO. Audit complet, optimisation on-site et off-site pour améliorer votre visibilité et augmenter votre trafic qualifié.' },
        { name: 'keywords', content: 'services SEO, référencement naturel, audit SEO, optimisation on-site, netlinking, backlinks, stratégie SEO, audit technique, Arynae' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Open Graph essentiels
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Services SEO - Experts en référencement naturel | Arynae' },
        { property: 'og:description', content: 'Découvrez nos services de référencement naturel SEO. Audit complet, optimisation on-site et off-site pour améliorer votre visibilité et augmenter votre trafic qualifié.' },
        { property: 'og:url', content: 'https://arynae.com/seo' },
        { property: 'og:locale', content: 'fr_FR' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
        // Twitter Card basiques
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Services SEO - Experts en référencement naturel | Arynae' },
        { name: 'twitter:description', content: 'Découvrez nos services de référencement naturel SEO. Audit complet, optimisation on-site et off-site pour améliorer votre visibilité et augmenter votre trafic qualifié.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com/seo' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/seo' }
      ],
      htmlAttrs: {
        lang: 'fr'
      }
    })
    
    return {}
  },
  data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
</script>


<style scoped>
.seo-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem; /* Augmenté le padding-top pour compenser l'absence de la navigation */
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block; /* Assure que le titre occupe toute la largeur */
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Intro Card with Illustration */
.text-and-illustration {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.text-and-illustration .text-content {
  flex: 1;
  min-width: 300px;
}

/* Conteneur d'illustration */
.illustration-container {
  flex: 0.7;  /* Changé de flex: 1 à flex: 2 pour donner plus d'espace à la vidéo */
  min-width: 200px;  /* Augmenté pour garantir plus d'espace horizontal */
  height: 300px;  /* Légèrement augmenté en hauteur */
  display: flex;
  justify-content: center;
  align-items: center;
}

.youtube-video {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.illustration-img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 10px;
}

/* Section Résultats */
/* Section Résultats */
.results-section {
  background-color: #f9fafb;
  padding: 3rem 2rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
}

.results-title {
  font-size: 1.75rem;
  color: #333;
  margin-bottom: 2rem;
}

.results-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.results-content {
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
}

.results-left {
  flex: 2;
  min-width: 450px;
}

.results-img {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.results-right {
  flex: 1;
  min-width: 300px;
}

.results-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 0;
}

.stats-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-top: 1rem;
}

.stat-item {
  flex: 1;
  min-width: 120px;
  text-align: center;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: bold;
  color: #2563EB;
  margin-bottom: 0.75rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #555;
  line-height: 1.4;
}

/* Titre des services */
.services-title {
  font-size: 2rem;
  margin: 4rem 0 2.5rem;
  color: #333;
  text-align: center;
  position: relative;
}

.services-title:after {
  content: "";
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #2563EB, #7C3AED);
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
}

.service-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.service-card:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(to right, #2563EB, #7C3AED);
}

.service-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.service-title {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
  font-weight: bold;
}

.service-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 1.5rem;
}

.service-features {
  margin-top: 1rem;
  padding-left: 0;
  list-style: none;
}

.service-features li {
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  color: #555;
  position: relative;
  padding-left: 1.5rem;
}

.service-features li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #2563EB;
  font-weight: bold;
}

/* Advantages Grid */
.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
}

.advantage-card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  position: relative;
}

.advantage-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.advantage-title {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
}

.advantage-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #555;
}

/* Testimonials Section - Style moderne */
.testimonials-section {
  padding: 5rem 0;
  background: #ffffff;
  margin: 5rem 0;
  position: relative;
  overflow: hidden;
}

.testimonials-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 10% 20%, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 90%);
  z-index: 0;
}

.testimonials-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;
  position: relative;
  z-index: 1;
}

.testimonials-title::after {
  content: "";
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  border-radius: 2px;
}

.testimonials-container {
  display: flex;
  gap: 2rem;
  padding: 1rem 4rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}

.testimonials-container::-webkit-scrollbar {
  display: none;
}

.testimonial-card {
  background: white;
  border-radius: 16px;
  min-width: 320px;
  flex: 1;
  position: relative;
  scroll-snap-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.testimonial-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 0 12px rgba(0, 0, 0, 0.06);
}

.testimonial-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 1.5rem 0.5rem;
}

.client-info {
  flex: 1;
}

.client-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
  color: #1a202c;
}

.client-company {
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 0.5rem;
}

.rating {
  display: flex;
  font-size: 1.1rem;
  color: #FFC107;
}

.testimonial-body {
  padding: 0.5rem 1.5rem 2rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #4A5568;
  flex: 1;
}

.testimonial-border {
  height: 5px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  width: 100%;
}

.dots-navigation {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #CBD5E0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot:hover {
  background-color: #A0AEC0;
}

.dot.active {
  background: linear-gradient(90deg, #2563EB, #7C3AED);
  width: 30px;
  border-radius: 10px;
}

/* Testimonials Section - Correction responsive */
.testimonials-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  padding: 1rem;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  padding-bottom: 1rem; /* Espace pour la scrollbar */
}

.testimonial-card {
  flex: 0 0 calc(33.333% - 1.5rem);
  min-width: 280px;
  background: white;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Cache la scrollbar sur certains navigateurs mais garde la fonctionnalité */
.testimonials-container::-webkit-scrollbar {
  height: 6px;
}

.testimonials-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.testimonials-container::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .testimonials-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.dots-navigation {
  display: none;
}

.dots-navigation {
  display: none; /* Cache les points s'ils ne sont pas nécessaires */
}

@media (max-width: 992px) {
  .testimonials-container {
    flex-direction: column;
    padding: 1rem;
    gap: 2rem;
  }
  
  .testimonial-card {
    width: 100%;
    margin-bottom: 1rem;
  }
}


.client-name {
  font-weight: bold;
  font-style: normal;
  margin-top: 1rem;
}

/* Call to Action */
.cta-container {
  text-align: center;
  margin: 3rem 0;
}

.cta-button {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: inline-block;
  max-width: 100%;
  word-wrap: break-word;
}

@media (max-width: 768px) {
  .cta-button {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
    width: 90%;
  }

  .text-and-illustration {
    flex-direction: column;
  }
 
  .text-and-illustration .text-content,
  .illustration-container {
    min-width: 0 !important;
    width: 100%;
  }
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .service-card {
    padding: 2rem;
    margin-bottom: 1.5rem;
  }

  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  .advantages-grid {
    grid-template-columns: 1fr;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .text-and-illustration {
    flex-direction: column;
  }
  
  .illustration-container {
    height: 200px; /* Hauteur réduite sur mobile */
  }
  
  .results-section {
    padding: 2rem 1rem;
  }
  
  .results-content {
  flex-direction: column;
  gap: 2rem;
}

.results-left, .results-right {
  min-width: 100%;
}
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  
  .stat-item {
    min-width: 100%;
  }
}
</style>